import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetchJson } from '../../apiClient';
import AddWineForm from './AddWineForm';
import './CellarView.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

const WINE_TYPES = ['All', 'Red', 'White', 'Rosé', 'Sparkling', 'Dessert', 'Fortified'];

function CellarView({ instance, showMessage }) {
  const { t } = useTranslation();
  const [wines, setWines] = useState([]);
  const [cellarMode, setCellarMode] = useState('my');
  const [typeFilter, setTypeFilter] = useState('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const loadWines = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const endpoint = cellarMode === 'household'
        ? '/api/cellar?includeHousehold=true'
        : '/api/cellar';
      const data = await apiFetchJson(instance, endpoint);
      setWines(unwrap(data));
    } catch (e) {
      console.error('Failed to load cellar:', e);
      setError(t('grapeEscape.cellar.loadError', 'Failed to load cellar.'));
    } finally {
      setLoading(false);
    }
  }, [instance, cellarMode, t]);

  useEffect(() => {
    loadWines();
  }, [loadWines]);

  const handleWineAdded = useCallback(() => {
    setShowAddForm(false);
    loadWines();
    showMessage(t('grapeEscape.cellar.wineAdded', 'Wine added to cellar! 🍷'));
  }, [loadWines, showMessage, t]);

  const filteredWines = typeFilter === 'All'
    ? wines
    : wines.filter(w => w.wineType === typeFilter || w.type === typeFilter);

  const totalBottles = filteredWines.reduce((sum, w) => sum + (w.quantity || 0), 0);
  const totalValue = filteredWines.reduce((sum, w) => sum + ((w.purchasePrice || 0) * (w.quantity || 0)), 0);

  if (showAddForm) {
    return (
      <AddWineForm
        instance={instance}
        onSave={handleWineAdded}
        onCancel={() => setShowAddForm(false)}
        showMessage={showMessage}
      />
    );
  }

  return (
    <div className="ge-cellar">
      {/* Cellar Toggle */}
      <div className="ge-cellar-toggle">
        <button
          className={`ge-toggle-btn ${cellarMode === 'my' ? 'active' : ''}`}
          onClick={() => setCellarMode('my')}
        >
          {t('grapeEscape.cellar.myCellar', 'My Cellar')}
        </button>
        <button
          className={`ge-toggle-btn ${cellarMode === 'household' ? 'active' : ''}`}
          onClick={() => setCellarMode('household')}
        >
          {t('grapeEscape.cellar.householdCellar', 'Household Cellar')}
        </button>
      </div>

      {/* Stats Bar */}
      <div className="ge-cellar-stats">
        <span className="ge-cellar-stat">
          🍾 {totalBottles} {t('grapeEscape.cellar.bottles', 'bottles')}
        </span>
        <span className="ge-cellar-stat">
          💰 {totalValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
        </span>
      </div>

      {/* Filters & Add Button */}
      <div className="ge-cellar-controls">
        <div className="ge-type-filters">
          {WINE_TYPES.map(type => (
            <button
              key={type}
              className={`ge-filter-chip ${typeFilter === type ? 'active' : ''}`}
              onClick={() => setTypeFilter(type)}
            >
              {t(`grapeEscape.cellar.type.${type.toLowerCase()}`, type)}
            </button>
          ))}
        </div>
        <button className="ge-btn-primary" onClick={() => setShowAddForm(true)}>
          + {t('grapeEscape.cellar.addWine', 'Add Wine')}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="ge-error">
          <span>{error}</span>
          <button onClick={loadWines}>↻</button>
        </div>
      )}

      {/* Loading */}
      {loading ? (
        <div className="ge-loading">
          <div className="ge-spinner" />
          <span>{t('grapeEscape.cellar.loading', 'Loading cellar...')}</span>
        </div>
      ) : filteredWines.length === 0 ? (
        <div className="ge-empty">
          {t('grapeEscape.cellar.empty', 'Your cellar is empty. Add your first bottle!')}
        </div>
      ) : (
        <div className="ge-wine-grid">
          {filteredWines.map(wine => (
            <div key={wine.id} className="ge-wine-card ge-card">
              <div className="ge-wine-card-header">
                <span className="ge-wine-name">{wine.wineName || wine.name || 'Unknown'}</span>
                <span className={`ge-wine-type-badge ${(wine.wineType || wine.type || '').toLowerCase()}`}>
                  {wine.wineType || wine.type || ''}
                </span>
              </div>
              <div className="ge-wine-details">
                {wine.vintage && <span>{wine.vintage}</span>}
                {wine.producer && <span>{wine.producer}</span>}
                {wine.region && <span>{wine.region}</span>}
              </div>
              <div className="ge-wine-card-footer">
                <span className="ge-wine-qty">×{wine.quantity || 0}</span>
                {wine.purchasePrice > 0 && (
                  <span className="ge-wine-price">
                    {wine.currency || ''} {wine.purchasePrice}
                  </span>
                )}
                {wine.status && (
                  <span className={`ge-wine-status ${wine.status.toLowerCase()}`}>
                    {wine.status}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CellarView;
